import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Tag } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getBlogArticles } from '../data/blogData';
import { useLanguage } from '../contexts/LanguageContext';

// Helper function to get locale for date formatting
const getDateLocale = (language) => {
  const localeMap = {
    'en': 'en-GB',
    'nl': 'nl-BE',
    'fr': 'fr-FR'
  };
  return localeMap[language] || 'fr-FR';
};

const Blog = () => {
  const { language, t } = useLanguage();
  const articles = getBlogArticles(language);

  const blogTexts = {
    fr: {
      title: 'Blog',
      subtitle: 'Actualités, analyses et perspectives sur la médiation d\'entreprise',
      backHome: 'Retour à l\'accueil',
      featured: 'Article vedette',
      readArticle: 'Lire l\'article'
    },
    en: {
      title: 'Blog',
      subtitle: 'News, analysis and perspectives on corporate mediation',
      backHome: 'Back to home',
      featured: 'Featured article',
      readArticle: 'Read article'
    },
    nl: {
      title: 'Blog',
      subtitle: 'Nieuws, analyses en perspectieven over bedrijfsbemiddeling',
      backHome: 'Terug naar home',
      featured: 'Uitgelicht artikel',
      readArticle: 'Lees artikel'
    }
  };

  const texts = blogTexts[language] || blogTexts.fr;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1e5f8c] to-[#2c7db5] text-white py-20">
        <div className="container mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {texts.backHome}
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{texts.title}</h1>
          <p className="text-xl text-white/90 max-w-3xl">
            {texts.subtitle}
          </p>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {articles.map((article) => (
            <Card key={article.id} className="hover:shadow-lg transition-shadow duration-300 flex flex-col">
              <CardHeader>
                {article.featured && (
                  <span className="inline-block bg-[#1e5f8c] text-white text-xs font-semibold px-3 py-1 rounded-full mb-3 w-fit">
                    {texts.featured}
                  </span>
                )}
                <CardTitle className="text-2xl mb-3 hover:text-[#1e5f8c] transition-colors">
                  <Link to={`/blog/${article.slug}`}>
                    {article.title}
                  </Link>
                </CardTitle>
                <CardDescription className="text-base line-clamp-3">
                  {article.excerpt}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-grow flex flex-col justify-between">
                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(article.date).toLocaleDateString(getDateLocale(language), {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {article.readingTime}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {article.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        <Tag className="w-3 h-3" />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <Link to={`/blog/${article.slug}`}>
                  <Button className="w-full bg-[#1e5f8c] hover:bg-[#164a6d]">
                    {texts.readArticle}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Blog;
